import { useLocation, useNavigate } from 'react-router-dom';
import { CloseRounded as CloseRoundedIcon } from '@mui/icons-material';
import styled, { css } from 'styled-components';
import { notifications, NotificationsDrawerBase } from './NotificationsComponents';
import { NotificationsForgotPassword } from './NotificationsForgotPassword';
import { Button, Chip, DrawerHeader, IconButton, Typography } from '../../design-system';
import { i18n } from '../../i18n';
import { useDeleteNotificationMutation, useReadNotificationMutation } from '../../hooks';
import { momentTextFormat } from '../../utils';
import { useStore } from '../../context';
import { showToRoles } from '../../utils';
import { Actions } from '../../store';
import { NotificationGetAllQuery } from '../../types';

let notificationData: any = null;
export const getNotificationData = () => {
  if (!notificationData) return null;

  const oldNotificationData = { ...notificationData };
  notificationData = null;

  return oldNotificationData;
};

const actionButtons = {
  SIGN_UP: {
    'Logistic Company': {
      text: i18n.t('button.addYardCompany'),
      path: '/dashboard/yardcompanies/add',
    },
    'Container Depot (Terminal)': {
      text: i18n.t('button.addYard'),
      path: '/dashboard/yards/add',
    },
    'Container Trader': {
      text: i18n.t('button.addClient'),
      path: '/dashboard/clients/add',
    },
    Other: { text: i18n.t('button.addClient'), path: '/dashboard/clients/add' },
  },
  SIGN_UP_BUY_CONTAINER: {
    'Logistic Company': {
      text: i18n.t('button.addYardCompany'),
      path: '/dashboard/yardcompanies/add',
    },
    'Container Depot (Terminal)': {
      text: i18n.t('button.addYard'),
      path: '/dashboard/yards/add',
    },
    'Container Trader': {
      text: i18n.t('button.addClient'),
      path: '/dashboard/clients/add',
    },
    Other: { text: i18n.t('button.addClient'), path: '/dashboard/clients/add' },
  },
  CLIENT_INTEREST_IN_YARD: {
    text: i18n.t('button.submit'),
    path: '/dashboard/clients',
  },
};

const NotificationsViewBase = styled(NotificationsDrawerBase)<{ isRead: boolean }>`
  & > *:last-child {
    place-self: initial;
    width: initial;
  }

  ${(props) =>
    props.isRead &&
    css`
      grid-template-rows: 6.8rem 1fr;
    `}
`;

const NotificationMessageWrapper = styled.div`
  display: grid;
  grid-auto-rows: max-content;
  grid-row-gap: var(--spacing-4);
  justify-items: left;

  overflow-y: overlay;

  & > p {
    margin-top: 1.6rem !important;
  }
`;

const NotificationButtons = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  gap: var(--spacing-4);
`;

const NotificationFooter = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: var(--spacing-4);
`;

const ChipCreatedAt = styled(Chip)`
  position: absolute;
  right: var(--spacing-4);
`;

type NotificationsViewProps = {
  isOpen: boolean;
  onClose: (isRefetch?: boolean) => void;
} & NotificationGetAllQuery['data'][0];

export const NotificationsView = ({
  isOpen,
  onClose,
  id,
  category,
  message,
  data,
  createdAt,
  readAt,
}: NotificationsViewProps) => {
  const {
    state: {
      user: { data: user },
    },
    dispatch,
  } = useStore();
  const deleteMutation = useDeleteNotificationMutation();
  const readMutation = useReadNotificationMutation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDeleteClick = () => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        onClose(true);
        dispatch({ type: Actions.SET_REFRESH_PAGE });
      },
    });
  };

  const handleReadClick = () => {
    readMutation.mutate(id, {
      onSuccess: () => {
        onClose(true);
        dispatch({ type: Actions.SET_REFRESH_PAGE });
      },
    });
  };

  const handleActionClick = (path: string) => {
    notificationData = { ...data };

    if (location.pathname === path) {
      dispatch({ type: Actions.SET_REFRESH_PAGE });
    }

    navigate(path);
    return;
  };

  return (
    <NotificationsViewBase isOpen={isOpen} isRead={!!readAt}>
      <DrawerHeader>
        <Typography variant={'textL'} weight={'bold'}>
          {notifications[category].title}
        </Typography>
        <IconButton onClick={() => onClose()} iconColor={'--gray-70'}>
          <CloseRoundedIcon />
        </IconButton>
      </DrawerHeader>
      <NotificationMessageWrapper>
        <ChipCreatedAt
          size={'small'}
          label={momentTextFormat(createdAt, user)}
          color={'lightblue'}
        />
        <Typography variant="textS" weight="medium" breakSpaces>
          {message?.trim()}
        </Typography>
        {(category === 'SIGN_UP' || category === 'SIGN_UP_BUY_CONTAINER') && (
          <NotificationButtons>
            <Button
              size={'medium'}
              data-color={'primary'}
              onClick={() =>
                handleActionClick(
                  actionButtons[category][
                    data.category as keyof (typeof actionButtons)[typeof category]
                  ].path,
                )
              }
            >
              {
                actionButtons[category][
                  data.category as keyof (typeof actionButtons)[typeof category]
                ].text
              }
            </Button>
            {!!data.link && (
              <Button
                size={'medium'}
                data-color={'primary'}
                onClick={() => window.open(data.link, '_blank')}
              >
                {i18n.t('button.link')}
              </Button>
            )}
          </NotificationButtons>
        )}
        {category === 'CLIENT_INTEREST_IN_YARD' && (
          <Button
            size={'medium'}
            data-color={'primary'}
            onClick={() => handleActionClick(actionButtons[category].path)}
          >
            {actionButtons[category].text}
          </Button>
        )}
        {category === 'FORGOT_PASSWORD' && (
          <NotificationsForgotPassword token={data.link.slice(data.link.indexOf('=') + 1)} />
        )}
      </NotificationMessageWrapper>
      {!readAt && !showToRoles(['YARD_COMPANY_VIEWER'], user) && (
        <NotificationFooter>
          <Button size={'medium'} data-color={'destructive'} onClick={handleDeleteClick}>
            {i18n.t('button.delete')}
          </Button>
          <Button size={'medium'} data-color={'primary'} onClick={handleReadClick}>
            {i18n.t('button.read')}
          </Button>
        </NotificationFooter>
      )}
    </NotificationsViewBase>
  );
};
